import { useDeferredValue, useMemo } from 'react';
import { filterListItems } from '../activitiesFilter';
import { issueStatusKey, type IPageFilters } from '../pageFilters';
import type { IListItem } from '../model/listItem';
import { planIssues, promiseRows } from './workPlanModel';

/**
 * The Work Plan's feed: the page's items narrowed by the panel exactly as it
 * narrows the Daily List, minus the date scope (the window replaces it) — the
 * committed activities as rows, the issues the lane holds (under their Status
 * checkboxes) as chips.
 * The rows keep every rung: the window's counts read them all, and the
 * Status checkboxes hide rungs downstream. Search is deferred like the
 * tracker's, so typing never waits on a relayout.
 */
export function usePlanFeed(items: IListItem[], filters: IPageFilters, today: string) {
  const search = useDeferredValue(filters.search);
  const rows = useMemo(() => {
    const panel = { ...filters, search, date: 'all' as const, types: ['activity' as const], status: null };
    return promiseRows(filterListItems(items, panel, today), today);
  }, [items, filters, search, today]);
  const issues = useMemo(
    () =>
      planIssues(filterListItems(items, { ...filters, search, date: 'all', types: ['issue'], status: null }, today)).filter(
        (issue) => !filters.hiddenStatuses.includes(issueStatusKey(issue.status)),
      ),
    [items, filters, search, today],
  );
  return { rows, issues };
}
